import { useEffect, useState } from 'react';
import { listMailingManualGroups, setMailingGroupMember, type MailingManualGroup } from '../api/affilmaster';
import type { MailingSpace } from '../api/mailing';
import { forgetMailingSubscription } from './MailingSubscriptionBadge';
import './mailing-group-picker.css';

export interface MailingGroupsValue {
  groups: MailingManualGroup[];
  selected: number[];
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const norm = (e: string | null | undefined) => (e ?? '').trim().toLowerCase();

export function MailingGroupsField({ email, currentEmail, value, onChange, space = 'affilmaster' }: {
  email: string | null | undefined;
  currentEmail: string;
  value: MailingGroupsValue | null;
  onChange: (v: MailingGroupsValue) => void;
  space?: MailingSpace;
}) {
  const [error, setError] = useState<string | null>(null);
  const original = norm(email);

  useEffect(() => {
    let cancelled = false;
    listMailingManualGroups(EMAIL_RE.test(original) ? original : '', space)
      .then((groups) => !cancelled && onChange({ groups, selected: groups.filter((g) => g.member).map((g) => g.id) }))
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : 'Načtení skupin selhalo'));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [original, space]);

  const hasEmail = EMAIL_RE.test(norm(currentEmail));
  const toggle = (id: number) => {
    if (!value) return;
    const selected = value.selected.includes(id) ? value.selected.filter((x) => x !== id) : [...value.selected, id];
    onChange({ ...value, selected });
  };

  return (
    <div className="am-groups-field">
      <label>Skupiny v mailingu</label>
      {error && <span className="am-group-picker-note is-error">{error}</span>}
      {!error && !value && <span className="am-group-picker-note">Načítám…</span>}
      {value && value.groups.length === 0 && <span className="am-group-picker-note">Žádná aktivní skupina nemá pravidlo Ruční přidání.</span>}
      {value && value.groups.length > 0 && (
        <div className="am-groups-field-list">
          {value.groups.map((g) => (
            <label key={g.id} className={`am-groups-field-chip${value.selected.includes(g.id) ? ' is-on' : ''}${hasEmail ? '' : ' is-disabled'}`}>
              <input type="checkbox" checked={value.selected.includes(g.id)} disabled={!hasEmail} onChange={() => toggle(g.id)} />
              <i style={{ background: g.color }} />
              <span>{g.name}</span>
            </label>
          ))}
        </div>
      )}
      {value && value.groups.length > 0 && !hasEmail && <span className="am-group-picker-note">Do skupiny lze zařadit jen kontakt s e-mailem.</span>}
    </div>
  );
}

export async function saveMailingGroups(prevEmail: string | null | undefined, nextEmail: string | null | undefined, value: MailingGroupsValue | null, space: MailingSpace = 'affilmaster'): Promise<void> {
  if (!value) return;
  const prev = norm(prevEmail);
  const next = norm(nextEmail);
  const nextOk = EMAIL_RE.test(next);
  const changed = prev !== next;
  for (const g of value.groups) {
    const want = nextOk && value.selected.includes(g.id);
    if (changed && g.member && EMAIL_RE.test(prev)) await setMailingGroupMember(g.id, prev, false, space);
    const isMember = changed ? false : g.member;
    if (nextOk && want !== isMember) await setMailingGroupMember(g.id, next, want, space);
  }
  if (prev) forgetMailingSubscription(prev, space);
  if (next) forgetMailingSubscription(next, space);
}
